import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';

import { customFetchV2 } from '../services/custom-fetch-v2';

interface PhoneInfo {
  number: string;
  numberFormatted: string;
  verified: boolean;
}

interface UserConfig {
  emailNotifications: boolean,
  smsNotifications: boolean,
  smsNotificationsAvailable: boolean,
  smsMonthlyQuota: number,
  smsMonthlyUsed: number,
  hideNoActiveDevicesAlert: boolean,
  hideMissingLocationDataAlert: boolean,
  emailReceipts: boolean,
}

interface AppFeedbackRequest {
  id: string;
}

interface UserInfo {
  firstName: string | null;
  lastName : string | null;
  phone: PhoneInfo | null;
  homebaseCountry: string | null;
  email: string | null;
  activeDeviceIds: string[];
  config: UserConfig;
  openAppFeedbackRequest?: AppFeedbackRequest;
  supportedFeatures: string[];
  apiAccessEnabled: boolean;
  inRecovery: boolean;
  stripeMigrationRequired: boolean;
}

interface GetCurrentUserResponse {
  firstName: string | null;
  lastName: string | null;
  defaultPhone: PhoneInfo | null;
  homebaseCountry: string | null;
  email: string | null;
  activeDeviceIds: string[];
  config: UserConfig;
  openAppFeedbackRequest?: AppFeedbackRequest;
  supportedFeatures: string[];
  apiAccessEnabled: boolean;
  inRecovery: boolean;
  stripeMigrationRequired: boolean;
}

// Define a constant for the query key
const CURRENT_USER_QUERY_KEY = ['useGetCurrentUser'];

/**
 * API query to retrieve user info.
 */
const getCurrentUser = async ({ skipReloadOnAuthError }: { skipReloadOnAuthError: boolean }): Promise<UserInfo | null> => {
  const response = await customFetchV2<GetCurrentUserResponse>('/sessions/me', {
    method: 'GET',
    skipReloadOnAuthError,
  });


  if (!response.ok) {
    throw new Error('Unable to get user');
  }

  const json = response.data;

  return {
    firstName: json.firstName,
    lastName: json.lastName,
    phone: json.defaultPhone,
    homebaseCountry: json.homebaseCountry,
    email: json.email,
    activeDeviceIds: json.activeDeviceIds,
    config: json.config,
    openAppFeedbackRequest: json.openAppFeedbackRequest,
    supportedFeatures: json.supportedFeatures,
    apiAccessEnabled: json.apiAccessEnabled,
    inRecovery: json.inRecovery,
    stripeMigrationRequired: json.stripeMigrationRequired,
  };
};

interface UseGetCurrentUserProps {
  refetchOnInterval?: boolean;
  refetchOnWindowFocus?: boolean;
  skipReloadOnAuthError?: boolean;
}

// Define a new return type that includes the invalidate function
type UseGetCurrentUserResult = UseQueryResult<UserInfo, unknown> & {
  invalidateUser: () => void;
};

/**
 * React hook that will fetch and refresh the current logged in user.
 * Also returns an invalidateUser function for convenient refetching.
 */
const useGetCurrentUser = (
  { refetchOnInterval = false, refetchOnWindowFocus = true, skipReloadOnAuthError = false }: UseGetCurrentUserProps = {}
): UseGetCurrentUserResult => {
  // If these aren't set then the query will refresh only on mount and refocus
  const refetchProps = {
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
  };
  
  const queryClient = useQueryClient();
  
  // Get the query result
  const queryResult = useQuery(CURRENT_USER_QUERY_KEY, () => getCurrentUser({ skipReloadOnAuthError }), {
    enabled: true,
    retry: false,
    ...(refetchOnInterval && refetchProps),
    refetchOnWindowFocus,
  });
  
  // Create the invalidate function
  const invalidateUser = () => {
    queryClient.invalidateQueries(CURRENT_USER_QUERY_KEY);
  };
  
  // Return both the query result and the invalidate function
  return {
    ...queryResult,
    invalidateUser,
  };
};

/** Exports */
export type { UserInfo, PhoneInfo, AppFeedbackRequest, UserConfig };
export { useGetCurrentUser };
